"use client";

import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Check } from "lucide-react";

const PricingSection = () => {
  const [isYearly, setIsYearly] = useState(false);

  const calculateYearlyPrice = (monthlyPrice: number) => {
    const yearlyPrice = monthlyPrice * 12;
    const discount = yearlyPrice * 0.25; // 25% discount
    return Math.round(yearlyPrice - discount);
  };

  const plans = [
    {
      name: "Essential",
      monthlyPrice: 299,
      yearlyPrice: calculateYearlyPrice(299),
      description: "Perfect for businesses just starting with data analytics",
      features: [
        "Monthly dashboard consultation",
        "One dashboard revision per month",
        "Access to all platform integrations",
        "Basic KPI tracking",
        "Email support",
        "Up to 3 dashboard views",
      ],
      buttonText: "Get Started",
      popular: false,
    },
    {
      name: "Professional",
      monthlyPrice: 599,
      yearlyPrice: calculateYearlyPrice(599),
      description: "For growing businesses needing flexible analytics",
      features: [
        "Unlimited dashboard revisions",
        "Dedicated success manager",
        "24/7 priority support",
        "Priority dashboard customization",
        "Advanced KPI tracking & insights",
        "Custom metric creation",
        "Up to 10 dashboard views",
        "Data export capabilities",
      ],
      buttonText: "Get Started",
      popular: true,
    },
    {
      name: "Enterprise",
      monthlyPrice: null,
      yearlyPrice: null,
      description: "Full-service solution for organizations with complex needs",
      features: [
        "Everything in Professional",
        "White-label dashboards",
        "Enterprise SSO",
        "Role-based access control",
        "Custom API integration support",
        "Unlimited dashboard views",
        "Team training sessions",
      ],
      buttonText: "Contact Sales",
      popular: false,
    },
  ];

  return (
    <div className="space-y-8">
      {/* Billing Toggle */}
      <div className="flex justify-center items-center space-x-4">
        <span
          className={`text-lg ${!isYearly ? "text-classic-blue font-semibold" : "text-gray-600"}`}
        >
          Monthly
        </span>
        <button
          onClick={() => setIsYearly(!isYearly)}
          className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-classic-blue focus:ring-offset-2"
          style={{ backgroundColor: isYearly ? "#0066FF" : "#CBD5E1" }}
        >
          <span
            className={`${
              isYearly ? "translate-x-6" : "translate-x-1"
            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
          />
        </button>

        <div className="flex items-center">
          <span
            className={`text-lg ${isYearly ? "text-classic-blue font-semibold" : "text-gray-600"}`}
          >
            Yearly
          </span>
          <span className="ml-2 inline-block bg-green-100 text-green-800 text-sm px-2 py-1 rounded-full">
            Save 25%
          </span>
        </div>
      </div>

      <div className="text-center text-gray-600">All prices in USD</div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {plans.map((plan, index) => (
          <Card
            key={index}
            className={`relative border-2 flex flex-col ${
              plan.popular
                ? "border-classic-blue shadow-[0_0_25px_rgba(0,122,255,0.2)]"
                : "border-gray-100"
            } hover:border-classic-blue transition-all duration-200`}
          >
            {plan.popular && (
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-classic-blue text-white text-sm py-1 px-3 rounded-full">
                  Most Popular
                </span>
              </div>
            )}
            <CardHeader>
              <CardTitle className="text-2xl font-bold">{plan.name}</CardTitle>
              <div className="mt-4">
                {plan.monthlyPrice ? (
                  <>
                    <div>
                      <span className="text-4xl font-bold">
                        $
                        {(isYearly
                          ? Math.round(plan.monthlyPrice * 0.75)
                          : plan.monthlyPrice
                        ).toLocaleString()}
                      </span>
                      <span className="text-gray-600">USD/mo</span>
                    </div>
                    {isYearly && (
                      <div className="text-sm text-green-600 mt-1">
                        ${plan.yearlyPrice.toLocaleString()} billed yearly
                      </div>
                    )}
                  </>
                ) : (
                  <span className="text-xl font-bold">Custom Pricing</span>
                )}
              </div>
              <CardDescription className="mt-4 text-base">
                {plan.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-grow flex flex-col">
              <ul className="space-y-4 flex-grow">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <Check className="w-5 h-5 mr-2 text-classic-blue flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
              <button
                className={`w-full mt-8 py-3 px-6 rounded-md font-semibold transition-all duration-200 ${
                  plan.popular
                    ? "bg-classic-blue text-white hover:bg-blue-700"
                    : "bg-white border-2 border-classic-blue text-classic-blue hover:bg-classic-blue hover:text-white"
                }`}
              >
                {plan.buttonText}
              </button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default PricingSection;
